export type HistoryBeatId =
  | "designer"
  | "straightPull"
  | "patent"
  | "m1908"
  | "failure"
  | "combat"
  | "doll";

export type HistoryBeat = {
  id: HistoryBeatId;
  kicker: string;
  year: string;
  title: string;
  body: string[];
  silhouette: "straight" | "blend" | "semi";
};

export const historyBeats: HistoryBeat[] = [
  {
    id: "designer",
    kicker: "Inventor and Namesake",
    year: "1859–1922",
    title: "Manuel Mondragón Montenegro",
    body: [
      "Born in Ixtlahuaca, State of Mexico. He left Chapultepec in 1880 as an artillery officer and spent the Porfiriato designing matériel, not chasing field commands. He also trained at France’s École spéciale militaire de Saint-Cyr — the schooling that later made SIG a natural partner.",
      "He designed the Saint-Chamond-Mondragón 75 mm howitzer, wrote fire-control manuals in 1904 and 1910, and sited coastal batteries at Salina Cruz and Puerto México. Director of Artillery from 1907; France awarded him the Légion d'honneur. One of eight children was the painter-poet Carmen Mondragón — Nahui Ollin.",
      "In February 1913 he joined the Decena Trágica coup against Francisco I. Madero, then briefly served as Secretary of War under Victoriano Huerta. Resigned in June, expelled in July, died in exile in San Sebastián, Spain, in 1922.",
    ],
    silhouette: "straight",
  },
  {
    id: "straightPull",
    kicker: "Predecessor",
    year: "1891–1894",
    title: "M1893 and M1894",
    body: [
      "Work began in 1891. Straight-pull patents: {patent:BE98947} (23 March 1892), {patent:FR221035} (20 April 1892), {patent:US557079} (filed 8 February 1893, issued 24 March 1896). Short-lived M1893: two rings of six radial lugs in helical receiver grooves — like AR bolts decades later — feeding an eight-round en-bloc clip in Mondragón’s 6.5×48mm.",
      "Survivor: SIG’s Model 1894 Type IIB, Rock Island Auction serial 2, marked SIG 1894. 21.5-inch barrel, no bayonet lug, two rows of five locking lugs after Swiss Rubin. Selector A closed-bolt single, L safe, R fires as the bolt slams home (Winchester 1897-style). 6-to-8-round en-bloc — Garand-like, 34 years early — in Colonel Rubin’s 5.2×68mm from Polte of Magdeburg.",
      "Mexican shops could not hold the tolerances. SIG in Neuhausen took the work with Díaz’s backing: fifty rifles in 1893, then two hundred M1894s.",
    ],
    silhouette: "straight",
  },
  {
    id: "patent",
    kicker: "Birth of the M1908",
    year: "1904–1907",
    title: "Evolving further: Self Loading",
    body: [
      "After the 1892 Belgian and French filings and the 1896 straight-pull grant, Mondragón filed U.S. application 219,989 on 8 August 1904. {patent:US853715} issued on 14 May 1907. It covers a gas-operated rifle that still locks with the same rotating bolt, and that can be switched back to hand-cycling if the gas system is cut off. In 1911 he added {patent:US1003119} — a spade-bayonet — beside the earlier knife bayonet.",
      "A piston under the barrel does not drive the bolt directly. Its stub strikes the charging handle; lugs on that handle cam the bolt open, the same helical lock as the straight-pull. A switch on the handle disconnects the gas system so the rifle can be run by hand and spare ammunition — a magazine cut-off built into the action. Díaz wanted the prestige of being first: a national army issued a semi-automatic shoulder rifle as standard.",
    ],
    silhouette: "blend",
  },
  {
    id: "m1908",
    kicker: "Adoption",
    year: "1908–1911",
    title: "Fusil Porfirio Díaz Sistema Mondragón Modelo 1908",
    body: [
      "Mexico adopted the design in 1908 and contracted SIG for 4,000 rifles in the 7×57mm Mauser service cartridge, at 160 Swiss francs apiece. They came with a bipod.",
      "The Mexican Army put it into service in 1911. Use in the Revolution is thin and uncertain. A few rifles may have been present at an ambush on Pancho Villa. Some sources give 1911 as the start of army use; two plates in Crónica Ilustrada Revolución Mexicana, Volume 1 (pp. 100 and 159), and William B. Edwards in Guns (1958), point to service as early as 1910.",
    ],
    silhouette: "semi",
  },
  {
    id: "failure",
    kicker: "Failure",
    year: "1911",
    title: "The order that never filled",
    body: [
      "Delivery never matched the 4,000-rifle contract. At most about forty samples reached Mexico; other estimates put pre-Revolution arrivals in the hundreds. At 160 Swiss francs apiece the cost was intolerable, and Mexico cancelled.",
      "The action itself was not the failure. Ian McCollum notes the rifles ran on European 7mm Mauser. Mexican ammunition did not: inconsistent powder and poorly drawn cases fouled the gas system. With decent ammunition, the same rifle works.",
    ],
    silhouette: "semi",
  },
  {
    id: "combat",
    kicker: "To Germany. To Air.",
    year: "1915–1918",
    title: "Flieger-Selbstladekarabiner 15",
    body: [
      "In 1915 the German military bought SIG’s leftover stock — up to the original 4,000, depending on how many had actually been built. Conversion to 7.92×57mm failed. In the trenches the action packed with mud. Germany issued the leftovers as aviation rifles for balloon troops and aircraft observers: two rifles per aircrew, redesignated Fl.-S.-K. 15, with Friedrich Blum’s 30-round drums.",
      "Once interrupter-geared machine guns arrived around 1917, the Mondragón left the cockpit for the Imperial Navy — destroyer crews and Seebataillon pioneers. Switzerland tried 7.5×55mm, a 12-round magazine, and a Hülsenfangkorb to catch brass. Observer mounts on the Häfeli DH and Blériot were equally brief.",
    ],
    silhouette: "semi",
  },
  {
    id: "doll",
    kicker: "Index 317",
    year: "2020",
    title: "El Demonia",
    body: [
      "Surviving rifles are scarce. Examples that come to market typically sell between $30,000 and $90,000.",
      "Girls’ Frontline introduced her in Dual Randomness Chapter 2 in August 2020. She drops from that chapter; she is not craftable.",
    ],
    silhouette: "semi",
  },
];
